import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Footer } from "../../components/common/Footer";
import { Header } from "../../components/common/Header";
import { ArrowUpIcon } from "../../assets/image/ArrowUpIcon";
import { MainDandruffIcon } from "../../assets/image/MainDandruffIcon";
import { DATA_RESOURCES } from "../../utils/data";
import { CATEGORY_RESOURCES, ENG_LANGUAGE } from "../../utils/variables";
import {
  fetchResourceCategories,
  fetchResourceCategory,
  fetchResourceContent,
} from "../../api";

export function ResourcesPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const [resourcesData, setResourcesData] = useState({});
  const [resourcesCategory, setResourcesCategory] = useState([]);
  const [filteredSerbianData, setFilteredSerbianData] = useState(DATA_RESOURCES);
  const [inputValue, setInputValue] = useState("");
  const [display, setDisplay] = useState("none");
  const [selectList, setSelectList] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [isOpen, setIsOpen] = useState(true);
  const [faqtab, setFaqtab] = useState([]);

  // Add this helper function for Serbian text search
  const filterSerbianData = (data, searchTerm, selectedCategories) => {
    if (!data || data.length === 0) return [];

    let filteredData = [...data];

    // Filter by selected categories
    if (selectedCategories.length > 0) {
      const selectedCategoryNames = selectedCategories.map(cat => cat.name);
      filteredData = filteredData.filter(item =>
          selectedCategoryNames.includes(item.category)
      );
    }

    // Filter by search term
    if (searchTerm && searchTerm.length > 0) {
      const searchTermLower = searchTerm.toLowerCase();
      filteredData = filteredData.filter(category => {
        // Search in category name
        if (category.category.toLowerCase().includes(searchTermLower)) {
          return true;
        }

        // Search in data items within the category
        const hasMatchingData = category.data.some(item => {
          return (
              (item.title && item.title.toLowerCase().includes(searchTermLower)) ||
              (item.subtitle && item.subtitle.toLowerCase().includes(searchTermLower)) ||
              (item.link && item.link.toLowerCase().includes(searchTermLower)) ||
              (item.linkAnother && item.linkAnother.toLowerCase().includes(searchTermLower))
          );
        });

        return hasMatchingData;
      });

      // If searching within categories, also filter the data items within each category
      filteredData = filteredData.map(category => ({
        ...category,
        data: category.data.filter(item => {
          return (
              (item.title && item.title.toLowerCase().includes(searchTermLower)) ||
              (item.subtitle && item.subtitle.toLowerCase().includes(searchTermLower)) ||
              (item.link && item.link.toLowerCase().includes(searchTermLower)) ||
              (item.linkAnother && item.linkAnother.toLowerCase().includes(searchTermLower))
          );
        })
      })).filter(category => category.data.length > 0); // Remove categories with no matching data
    }

    return filteredData;
  };

  const handleFilterArray = (data) => {
    setFaqtab((prevFaqtab) => {
      const isInFaqtab = prevFaqtab.some((e) => e.name === data);
      const newFaqtab = isInFaqtab
          ? prevFaqtab.filter((e) => e.name !== data)
          : [...prevFaqtab, { name: data }];

      return newFaqtab;
    });
  };

  const fetchAllData = async () => {
    try {
      const result = await fetchResourceContent();
      setResourcesData(result.data);

      const resultCategories = await fetchResourceCategories();
      setCategoriesData(resultCategories.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchAllData();
    handleCategoryData();
  }, []);

  const handleCategoryData = async () => {
    if (language === ENG_LANGUAGE) {
      // Use API for English
      setResourcesCategory([]);
      try {
        const result = await fetchResourceCategory(selectList, inputValue);
        setResourcesCategory(result.data);
      } catch (error) {
        console.error("Error fetching resource category:", error);
      }
    } else {
      // Use frontend filtering for Serbian
      const filteredData = filterSerbianData(DATA_RESOURCES, inputValue, selectList);
      setFilteredSerbianData(filteredData);
    }
  };

  // Optional: Add automatic search on input change (you can remove this if you want only manual search)
  // useEffect(() => {
  //   const debounceTimer = setTimeout(() => {
  //     handleCategoryData();
  //   }, 300);
  //   return () => clearTimeout(debounceTimer);
  // }, [inputValue, selectList, language]);

  const toggleAddCategory = (data) => {
    let sum = false;
    const updatedSelectList = [...selectList];

    updatedSelectList.forEach((e, index) => {
      if (e.name === data) {
        sum = true;
        updatedSelectList.splice(index, 1);
      }
    });

    if (!sum) {
      updatedSelectList.push({ name: data });
    }

    setSelectList(updatedSelectList);
  };

  const handleDisplay = () => {
    setIsOpen((prevOpen) => !prevOpen);
    setDisplay((prevDisplay) => (prevDisplay === "none" ? "block" : "none"));
  };

  const handleCloseDropdownOutside = (event) => {
    const dropdown = document.getElementById("cate");
    const svgIcon = document.getElementById("svgIcon");
    if (
        dropdown &&
        !dropdown.contains(event.target) &&
        svgIcon &&
        !svgIcon.contains(event.target)
    ) {
      setIsOpen(false);
      setDisplay("none");
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleCloseDropdownOutside);

    return () => {
      document.removeEventListener("click", handleCloseDropdownOutside);
    };
  }, []);

  return (
      <>
        <Header />
        <div className="container-fluid resource_tital">
          <div className="container">
            {resourcesData.title && (
                <h2>{t("resources.title", { defaultValue: resourcesData.title })}</h2>
            )}
            {resourcesData.description && (
                <div
                    dangerouslySetInnerHTML={{
                      __html: t("resources.description", {
                        defaultValue: resourcesData.description,
                      }),
                    }}
                    className="workus"
                />
            )}
          </div>
        </div>
        <div className="container-fluid">
          <div className="container">
            <div className="resource_content">
              <div className="resource_search">
                <label>
                  {t("Explore resources")}
                  <Link
                      to=""
                      onClick={(e) => {
                        e.preventDefault();
                        setInputValue("");
                        setSelectList([]);
                        // Reset to show all data
                        if (language === ENG_LANGUAGE) {
                          window.location.reload();
                        } else {
                          setFilteredSerbianData(DATA_RESOURCES);
                        }
                      }}
                  >
                    {t("Clear all filters")}
                  </Link>
                </label>
                <div className="form_group">
                  <div className="inp">
                    <input
                        className="keyword"
                        type="text"
                        placeholder={t("keyword")}
                        onChange={(e) => setInputValue(e.target.value)}
                        value={inputValue}
                    />
                    {inputValue.length > 0 && (
                        <span
                            onClick={(e) => {
                              setInputValue("");
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                      ×
                    </span>
                    )}
                  </div>

                  <div className="customselect inp">
                    <input
                        className="keyword"
                        type="text"
                        placeholder={t("Category")}
                        value={selectList.map((e) => e.name).join(", ")}
                        readOnly
                    />
                    <div
                        className={`option ${isOpen ? "open" : ""}`}
                        id="cate"
                        style={{ display: display }}
                    >
                      {language === ENG_LANGUAGE
                          ? categoriesData.map((e, index) => {
                            return (
                                <p
                                    key={index}
                                    className={
                                      categoriesData.length === 1 + index
                                          ? "border"
                                          : ""
                                    }
                                >
                                  <input
                                      type="checkbox"
                                      checked={selectList.some(item => item.name === e.category)}
                                      onChange={() => toggleAddCategory(e.category)}
                                  />
                                  <h3>{e.category}</h3>
                                  <span></span>
                                </p>
                            );
                          })
                          : CATEGORY_RESOURCES.map((e, index) => {
                            return (
                                <p
                                    key={index}
                                    className={
                                      CATEGORY_RESOURCES.length === 1 + index
                                          ? "border"
                                          : ""
                                    }
                                >
                                  <input
                                      type="checkbox"
                                      checked={selectList.some(item => item.name === e.category)}
                                      onChange={() => toggleAddCategory(e.category)}
                                  />
                                  <h3>{e.category}</h3>
                                  <span></span>
                                </p>
                            );
                          })}
                      <div
                          className="clr inp"
                          onClick={(e) => {
                            setSelectList([]);
                            setInputValue("");
                          }}
                      >
                        {t("Clear All")} <span>×</span>
                      </div>
                    </div>

                    <span onClick={handleDisplay} id="svgIcon">
                    <ArrowUpIcon />
                  </span>
                  </div>
                </div>
                <div className="clear">
                  <button
                      onClick={(e) => {
                        handleCategoryData();
                        handleDisplay("cate");
                      }}
                  >
                    <MainDandruffIcon width={20} height={21} color={"#A98D4B"} />
                    {t("text-search")}
                  </button>
                </div>
              </div>
              <div className="resources_tabs">
                {(language === ENG_LANGUAGE ? resourcesCategory : filteredSerbianData).length >= 1 ? (
                    language === "en" ? (
                        resourcesCategory.map((data, index) => {
                          return (
                              <div className="tabs" key={index}>
                                <h2
                                    onClick={(e) =>
                                        handleFilterArray("tab" + index, "icon" + index)
                                    }
                                >
                                  {data.category}
                                  <img
                                      src="./images/down_pur.svg"
                                      id={"icon" + index}
                                      alt="toggle"
                                  />
                                </h2>
                                <div className="tab_open" id={"tab" + index}>
                                  {data.data.map((data2, index2) => {
                                    return (
                                        <div key={index2}>
                                          {data2.title ? <h3>{data2.title}</h3> : <br />}
                                          <h4>{data2.subtitle}</h4>
                                          <a href={data2.link} target="_blank" rel="noopener noreferrer">
                                            {data2.link}
                                          </a>
                                          <br />
                                        </div>
                                    );
                                  })}
                                </div>
                              </div>
                          );
                        })
                    ) : (
                        filteredSerbianData.map((data, index) => {
                          return (
                              <div className="tabs" key={index}>
                                <h2
                                    onClick={(e) =>
                                        handleFilterArray("tab" + index, "icon" + index)
                                    }
                                >
                                  {data.category}
                                  <img
                                      src="./images/down_pur.svg"
                                      id={"icon" + index}
                                      alt="toggle"
                                  />
                                </h2>
                                <div className="tab_open" id={"tab" + index}>
                                  {data.data.map((data2, index2) => {
                                    return (
                                        <div key={index2}>
                                          {data2.title ? <h3>{data2.title}</h3> : <br />}
                                          <h4>{data2.subtitle}</h4>
                                          <ul>
                                            <li style={{ marginBottom: "4px" }}>
                                              <a href={data2.link} target="_blank" rel="noopener noreferrer">
                                                {data2.link}
                                              </a>
                                            </li>
                                            {data2.linkAnother && (
                                                <li>
                                                  <a href={data2.linkAnother} target="_blank" rel="noopener noreferrer">
                                                    {data2.linkAnother}
                                                  </a>
                                                </li>
                                            )}
                                          </ul>
                                          <br />
                                        </div>
                                    );
                                  })}
                                </div>
                              </div>
                          );
                        })
                    )
                ) : (
                    <p>
                      {t("current-criteria-found")}
                    </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </>
  );
}